import React, { Suspense, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { toUpper } from 'lodash';

import { useStores } from '@/hooks/use-stores';
import { permissions } from '@/constants';
import { ComponentTierName, StationTier } from '@/constants/enums';
import { checkTierPermission } from '@/utils/util-functions';
import { getDynamicComponent } from '@/constants/componentsMap';
import { SpinCentered } from '@/comp';
import { useDeviceIdFromUrl } from '@/hooks/useDeviceIdFromUrl';

import ProactiveEngineActivationBox from './ProactiveEngineActivationBox';
import ControlCenterKPIS from './ControlCenterKPIS';
import ControlCenterAssets from './ControlCenterAssets/ControlCenterAssets';
import CustomGauges from './CustomGauges';
import { Container } from './StationControlCenter.styles';

const StationControlCenter = observer(() => {
  const { proactiveEngineStore, permissionStore, stationStore } = useStores();
  const [isProactiveEngineAuthorized, setIsProactiveEngineAuthorized] = useState(false);
  const [isCustomGaugesExpanded, setIsCustomGaugesExpanded] = useState(false);
  const shouldRestrictProactiveEngineActivationBox = checkTierPermission(
    ComponentTierName.ProactiveEngine,
    stationStore,
  );
  const { t } = useTranslation();

  const currentTier = stationStore?.stationTier;

  const ProactiveEngineCommandsLog = getDynamicComponent(ComponentTierName.ProactiveEngineCommandsLog, currentTier);

  const hasAccessToCustomGauges = () => {
    return permissionStore.isAuth(permissions.VIEW_CUSTOM_GAUGES);
  };

  useEffect(() => {
    setIsProactiveEngineAuthorized(permissionStore.isAuth(permissions.UPDATE_STATION_CONTROL));
  }, [permissionStore.permissions]);

  const isDevicesReady = !stationStore.isLoading && !!stationStore.currentStation?.devices?.length;
  useDeviceIdFromUrl(isDevicesReady, stationStore.currentStation?.devices?.length || 0);

  const tabItems = [
    {
      label: t('Assets'),
      key: 'assets',
      children: (
        <div className="tab-assets-control-center">
          {stationStore.isLoading ? <SpinCentered /> : <ControlCenterAssets />}
        </div>
      ),
    },
    ...(![StationTier.Monitor, StationTier.Asset].includes(currentTier)
      ? [
          {
            label: t('Activity Log'),
            key: 'activity-log',
            children: (
              <Suspense>
                <ProactiveEngineCommandsLog />
              </Suspense>
            ),
          },
        ]
      : []),
  ];

  return (
    <Container>
      <div className="top">
        <ControlCenterKPIS
          isCustomGaugesExpanded={isCustomGaugesExpanded}
          setIsCustomGaugesExpanded={setIsCustomGaugesExpanded}
        />
        <div
          {...(proactiveEngineStore.proactiveEngineState &&
          toUpper(proactiveEngineStore.proactiveEngineState) !== 'UNAVAILABLE' &&
          isProactiveEngineAuthorized &&
          !shouldRestrictProactiveEngineActivationBox
            ? {}
            : { className: 'proactive-engine-box' })}
        >
          <ProactiveEngineActivationBox />
        </div>
      </div>
      {hasAccessToCustomGauges() && (
        <div className="middle">
          <CustomGauges isCustomGaugesExpanded={isCustomGaugesExpanded} />
        </div>
      )}

      <div className="bottom">
        <div className="tabs">
          <Tabs
            animated={{
              inkBar: true,
              tabPane: false,
            }}
            items={tabItems}
          />
        </div>
      </div>
    </Container>
  );
});

export default StationControlCenter;
